"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { columns } from "./(products)/columns";
import { DataView } from "@/components/common/DataView";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import {
  attachWholesalerProductRequest,
  fetchWholesalerProductRequest,
  removeSelectedProductIds,
} from "@/store/slices/wholesalerProducts.slice";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Label } from "@/components/ui/label";
import { InputPrimary } from "@/components/common/InputPrimary";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Trash2 } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { formatPaise } from "@team-shiphouse/shiphouse-sdk/client";
import { useGlobalLoading } from "@/hooks/useGlobalLoading";

export interface WholesalerProductFilters {
  category?: string;
  status?: string;
  price_min?: number;
  price_max?: number;
}

const ProductsPage: React.FC<{ id: string; title: string }> = ({ id, title }) => {
  const dispatch = useAppDispatch();
  const { byWholesalerId, selectedProductIds } = useAppSelector((state) => state.wholesalerProducts);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters] = useState<WholesalerProductFilters>({});
  const [value, setValue] = useState<number>(0);
  const [type, setType] = useState<string>("percentage");
  const [paymentType, setPaymentType] = useState<string[]>(["PREPAID"]);
  const [removedVariants, setRemovedVariants] = useState<Record<string, Set<string>>>({});
  const { showLoading, hideLoading } = useGlobalLoading();
  const pageSize = 10;

  const { data, pagination, loading, error } = byWholesalerId?.[id] || { data: [], pagination: { page: 0, total: 0, pageSize: 0, totalPages: 0 }, loading: false, error: null };

  const fetchProducts = useCallback(() => {
    const hasSearch = searchQuery.trim();
    const requestParams: any = { id, page: pageIndex + 1, pageSize: pageSize, filters: { ...appliedFilters } };
    if (hasSearch) requestParams.filters.title = searchQuery;
    dispatch(fetchWholesalerProductRequest(requestParams));
  }, [appliedFilters, searchQuery, pageIndex, dispatch, id]);

  const currentData = data || [];
  const currentPagination = { totalPages: pagination?.totalPages || 1, currentPage: (pagination?.page || 0) + 1, totalItems: pagination?.total || 0, pageSize: pagination?.pageSize || 10, total: pagination?.total || 0 };
  const currentLoading = loading;

  useEffect(() => { 
    // Only fetch if we don't have data OR if search/filters/page changed
    if ((currentData?.length || 0) === 0 || pageIndex > 0 || searchQuery.trim() || Object.keys(appliedFilters).length > 0) {
      fetchProducts(); 
    }
  }, [pageIndex, searchQuery, appliedFilters, currentData?.length]);

  useEffect(() => { setPageIndex(0); }, [searchQuery, appliedFilters]);

  useEffect(() => {
    if (!loading && !error && Object.keys(selectedProductIds).length === 0) hideLoading();
    if (error) hideLoading();
  }, [loading, error, hideLoading, selectedProductIds]);

  const handleSearch = (query: string) => { setSearchQuery(query); setPageIndex(0); };
  const handleRowClick = () => {};
  const handlePageChange = (newPageIndex: number) => setPageIndex(newPageIndex);

  const RemoveAll = () => { dispatch(removeSelectedProductIds(Object.keys(selectedProductIds))); };

  const HandlerToAdd = () => {
    showLoading("Importing selected products...");
    dispatch(attachWholesalerProductRequest({ id: id, margin: value, margin_type: type, payment_type: paymentType, product_ids: Object.keys(selectedProductIds) }));
  };

  return (
    <>
      <div className="bg-secondary-background p-[18px] border rounded-[16px]">
        <DataView
          columns={columns}
          data={currentData}
          pagination={currentPagination}
          pageIndex={pageIndex}
          onPageChange={handlePageChange}
          loading={currentLoading || false}
          error={error?.message || null}
          title={title}
          description="Manage products from this wholesaler"
          searchPlaceholder="Search products..."
          searchQuery={searchQuery}
          onSearch={handleSearch}
          onRowClick={handleRowClick}
          enableColumnResizing={true}
          enableRowSelection={false}
          emptyMessage="No products found."
          loadingMessage="Loading products..."
          actionButtons={[
            {
              label: "Refresh",
              icon: <RefreshCw className="h-3 w-3" />,
              onClick: fetchProducts,
              disabled: currentLoading,
              variant: "outline",
            },
          ]}
        />
      </div>

      {Object.keys(selectedProductIds).length > 0 && (
        <div className="flex justify-end gap-2 p-4">
          <ButtonOutline className="w-[212px] rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-primary-foreground" onClick={RemoveAll}>
            Discard
          </ButtonOutline>
          <Dialog>
            <DialogTrigger asChild>
              <ButtonPrimary className="w-[212px] rounded-full">Continue</ButtonPrimary>
            </DialogTrigger>
            <DialogContent className="p-0 overflow-hidden max-w-[1000px] bg-card max-h-[90vh] flex flex-col">
              <DialogHeader className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5 flex-shrink-0">
                <DialogTitle className="text-white text-xl font-semibold">Import Products to Inventory</DialogTitle>
                <p className="text-white/80 text-sm mt-1">Configure pricing and payment options for {Object.keys(selectedProductIds).length} selected product(s)</p>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Global Margin Settings */}
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                      Global Margin & Payment Settings
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="margin" className="text-xs font-medium text-muted-foreground">Margin Value (Applied to all products & variants)</Label>
                        <div className="flex gap-2">
                          <InputPrimary 
                            value={value} 
                            onChange={(e) => setValue(Number(e.target.value))} 
                            type="number" 
                            className="!h-11 flex-1" 
                            placeholder="Enter margin" 
                          />
                          <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="!bg-primary rounded-lg text-white !h-11 w-24 border-0">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent className="bg-card">
                              <SelectItem value="percentage">%</SelectItem>
                              <SelectItem value="fixed">₹</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">Payment Methods</Label>
                        <div className="flex flex-wrap gap-3 pt-1">
                          {[
                            { id: "PREPAID", label: "Prepaid" },
                            { id: "SEMIPREPAID", label: "Semi Prepaid" },
                            { id: "POSTPAID", label: "COD" }
                          ].map((method) => (
                            <div key={method.id} className="flex items-center gap-2">
                              <Checkbox 
                                id={`bulk-${method.id.toLowerCase()}`}
                                checked={paymentType.includes(method.id)} 
                                onCheckedChange={(checked) => setPaymentType((prev) => 
                                  checked ? [...prev, method.id] : prev.filter((item) => item !== method.id)
                                )} 
                                className="w-4 h-4 rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                              />
                              <Label htmlFor={`bulk-${method.id.toLowerCase()}`} className="text-sm cursor-pointer">{method.label}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products List */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                      Selected Products ({Object.keys(selectedProductIds).length})
                    </h3>
                    
                    <Accordion type="multiple" className="space-y-2">
                      {Object.entries(selectedProductIds).map(([productId, product]) => {
                        const variants = product.variants && product.variants.length > 0 
                          ? product.variants 
                          : [{
                              id: product.id,
                              title: "Default",
                              price: product.price || 0,
                              sku: product.sku || "",
                              inventory_quantity: 0,
                            }];

                        const productRemovedVariants = removedVariants[productId] || new Set();

                        const calculateNewPrice = (originalPrice: number, margin: number, marginType: string) => {
                          // Margin is in rupees, prices are in paise, so multiply fixed margin by 100
                          if (marginType === "percentage") {
                            return originalPrice + (originalPrice * margin / 100);
                          } else {
                            return originalPrice + (margin * 100);
                          }
                        };

                        const calculateProfit = (originalPrice: number, newPrice: number) => {
                          return newPrice - originalPrice;
                        };

                        const toggleVariantRemoval = (variantId: string) => {
                          setRemovedVariants(prev => {
                            const productVariants = new Set(prev[productId] || new Set());
                            if (productVariants.has(variantId)) {
                              productVariants.delete(variantId);
                            } else {
                              productVariants.add(variantId);
                            }
                            return { ...prev, [productId]: productVariants };
                          });
                        };

                        const totalProfit = variants
                          .filter((v: any) => !productRemovedVariants.has(v.id || ""))
                          .reduce((sum: number, variant: any) => {
                            const originalPrice = variant.price || 0;
                            const newPrice = calculateNewPrice(originalPrice, value, type);
                            return sum + calculateProfit(originalPrice, newPrice);
                          }, 0);

                        return (
                          <div key={productId} className="border border-border/50 rounded-lg overflow-hidden bg-background">
                            {/* Product Summary Card */}
                            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 border-b border-primary/20">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                    {product.images && product.images.length > 0 ? (
                                      <img 
                                        src={getAsset(product.images[0], { width: 120, height: 120, fit: 'cover' })} 
                                        alt={product.title} 
                                        className="w-full h-full object-cover" 
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Package className="h-8 w-8 text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-base">{product.title}</h4>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {variants.filter((v: any) => !productRemovedVariants.has(v.id || "")).length} variant(s) • Total Profit: <span className="font-semibold text-green-600">{formatPaise(totalProfit)}</span>
                                    </p>
                                  </div>
                                </div>
                                <DialogTrigger asChild>
                                  <ButtonOutline 
                                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white" 
                                    onClick={() => dispatch(removeSelectedProductIds([productId]))}
                                  >
                                    Remove Product
                                  </ButtonOutline>
                                </DialogTrigger>
                              </div>
                            </div>

                            {/* Variants Accordion */}
                            <AccordionItem value={productId} className="border-0">
                              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors data-[state=open]:bg-muted/50">
                                <div className="flex items-center justify-between w-full pr-2">
                                  <div className="flex items-center gap-3">
                                    <Package className="h-5 w-5 text-primary" />
                                    <div className="text-left">
                                      <h4 className="font-semibold text-sm">View Variants</h4>
                                      <p className="text-xs text-muted-foreground mt-0.5">{variants.length} variant(s) available</p>
                                    </div>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Click to expand
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="p-0">
                                <div className="bg-background">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-muted/50 hover:bg-muted/50 border-t">
                                        <TableHead className="font-semibold w-[80px]">Image</TableHead>
                                        <TableHead className="font-semibold">Name</TableHead>
                                        <TableHead className="font-semibold w-[140px]">Original Price</TableHead>
                                        <TableHead className="font-semibold w-[140px]">New Price</TableHead>
                                        <TableHead className="font-semibold w-[140px]">Total Profit</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {variants.map((variant: any, index: number) => {
                                        const variantId = variant.id || `variant-${index}`;
                                        const isRemoved = productRemovedVariants.has(variantId);
                                        const originalPrice = variant.price || 0;
                                        const newPrice = calculateNewPrice(originalPrice, value, type);
                                        const profit = calculateProfit(originalPrice, newPrice);

                                        return (
                                          <TableRow 
                                            key={variantId} 
                                            className={`hover:bg-muted/30 ${isRemoved ? 'opacity-50 bg-muted/20' : ''}`}
                                          >
                                            <TableCell className="py-3">
                                              <div className="w-14 h-14 rounded-md overflow-hidden bg-muted">
                                                {product.images && product.images.length > 0 ? (
                                                  <img 
                                                    src={getAsset(product.images[0], { width: 100, height: 100, fit: 'cover' })} 
                                                    alt={variant.title} 
                                                    className="w-full h-full object-cover" 
                                                  />
                                                ) : (
                                                  <div className="w-full h-full flex items-center justify-center">
                                                    <Package className="h-6 w-6 text-muted-foreground" />
                                                  </div>
                                                )}
                                              </div>
                                            </TableCell>
                                            <TableCell className="py-3">
                                              <div>
                                                <p className="font-medium text-sm">{variant.title}</p>
                                                <p className="text-xs text-muted-foreground mt-1">SKU: {variant.sku || "N/A"}</p>
                                              </div>
                                            </TableCell>
                                            <TableCell className="py-3">
                                              <span className="font-semibold text-sm">{formatPaise(originalPrice)}</span>
                                            </TableCell>
                                            <TableCell className="py-3">
                                              <span className="font-semibold text-sm text-primary">{formatPaise(newPrice)}</span>
                                            </TableCell>
                                            <TableCell className="py-3">
                                              <span className="font-semibold text-sm text-green-600">{formatPaise(profit)}</span>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </div>
                        );
                      })}
                    </Accordion>
                  </div>
                </div>
              </div>

              <DialogFooter className="justify-between p-6 pt-4 border-t border-border/50 flex-shrink-0 bg-muted/20 flex flex-row gap-3">
                <div className="text-sm text-muted-foreground">
                  {Object.keys(selectedProductIds).length} product(s) will be imported
                </div>
                <div className="flex gap-3">
                  <DialogTrigger asChild>
                    <ButtonOutline 
                      type="button" 
                      className="px-6 rounded-lg border-2 hover:bg-muted"
                    >
                      Cancel
                    </ButtonOutline>
                  </DialogTrigger>
                  <ButtonPrimary 
                    type="button" 
                    className="px-6 rounded-lg" 
                    onClick={HandlerToAdd}
                  >
                    Import Products
                  </ButtonPrimary>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
